"use client";
import MerchantLinkButton from "@/components/merchantLinkButton/merchantLinkButton";
import { NavigationBar } from "@/components/navigationBar/navigationBar";
import { NotAcceptingOrdersBanner } from "@/components/notAcceptingOrdersBanner/notAcceptingOrdersBanner";
import { Surcharge } from "@/components/surcharge/surcharge";
import getCustomerDetails from "@/dataLayer/getCustomerDetails";
import getDefaultAddress from "@/dataLayer/getDefaultAddress";
import { Address } from "@/dto/address";
import {
  DayPart,
  DayPartMenuCategories,
  DayPartMenuCookie,
} from "@/dto/dayPartMenu";
import { Service } from "@/dto/service";
import { OrderType, ServiceType } from "@/constants/enum";
import { cloverColorTheme } from "@/utils/cloverColorTheme";
import { getFromLocalStorage } from "@/utils/localStorageUtils";
import { canMerchantAcceptOrders } from "@/utils/scheduleOrderUtils";
import {
  getFromSessionStorage,
  removeFromSessionStorage,
  saveToSessionStorage,
} from "@/utils/sessionStorageUtils";
import { LocalStorageKey, SessionStorageKey } from "@/utils/storageKeys";
import { getTimezoneAbbreviation } from "@/utils/timeZoneAbbreviation";
import { throttle, isEmpty } from "lodash";
import { useFormatter, useLocale, useTranslations } from "next-intl";
import { usePathname, useSearchParams } from "next/navigation";
import { use, useRef, useEffect, useState, useMemo } from "react";
import HeaderClockIcon from "@/components/_icons/headerClockIcon";
import { CookiesBanner } from "./cookiesBanner";
import DownArrowIcon from "@/components/_icons/downArrowIcon";
import { HeaderMerchantInfoContainer } from "./headerMerchantInfoContainer";
import { BusinessHoursModal } from "./hoursModal";
import { buildScanToOrderSearchParams } from "@/utils/scanToOrderUtils";
import useMediaQuery from "@/utils/useMediaQuery";
import { useHeaderHeight } from "@/app/providers/navigationProvider";
import pom from "@/utils/pom";
import { applyDataTestId } from "@/utils/testUtils";
import clsx from "clsx";
import { Category } from "@/dto/category";
import { setDPMenuCookie } from "@/app/actions";
import { useCookies } from "react-cookie";
import { convertTimeStringToDate } from "@/utils/dateTimeUtils";
import {
  ROUTE_ALL,
  ROUTE_CATEGORIES_ALL,
  ROUTE_CHECKOUT,
  ROUTE_CONFIRMATION,
  ROUTE_CUSTOMER_PROFILE,
  ROUTE_GUEST_CHECKOUT,
  ROUTE_HOME,
  ROUTE_MENU_ALL,
  ROUTE_MY_APPOINTMENTS,
  ROUTE_MY_ORDERS,
  ROUTE_SERVICES,
  ROUTE_SERVICES_ALL,
  ROUTE_SHOP,
  UNCATEGORISED_CATEGORY_ID,
  ROUTE_GIFTCARD,
} from "@/utils/constants";
import { useNativeActions } from "@/utils/useNativeActions";
import { CmaHeader } from "./cmaHeader";
import { headerRegistry } from "./headerRegistry";
import ClockIcon from "../_icons/clockIcon";
import ChevronDownIcon from "../_icons/chevronDownIcon";

export interface HeaderProps {
  merchantLogo?: string;
  merchantName?: string;
  merchant: any;
  menu: any;
  deleteSession: any;
  retailCategories?: any;
  serviceCategories?: any;
  redirectToGiftCard: boolean;
  pickupTimePromise?: Promise<any>;
  dayPartMenus?: DayPart[];
  dayPartMenuCategories?: DayPartMenuCategories[];
  paymentConfigPromise?: Promise<any>;
  preferencesPromise?: Promise<any>;
}

interface TimeSlot {
  start: string;
  end: string;
}

interface TimeSlotProps extends TimeSlot {
  abbreviation: string;
}

interface MainTimeSlotProps {
  todayHours: TimeSlot[];
  abbreviation: string;
}

const commonClasses = ["text-[#373737]", "items-right", "cursor-pointer"].join(
  " "
);

const TimeSlot = ({ start, end, abbreviation }: TimeSlotProps) => {
  const t = useTranslations("Header");
  const locale = useLocale();
  const format = useFormatter();

  const getLocalizedTimeData = (timeStr: string) => {
    const luxonTime = convertTimeStringToDate(timeStr, abbreviation);

    const formattedTime = format.dateTime(luxonTime.toJSDate(), {
      hour: "numeric",
      minute: "2-digit",
      timeZone: abbreviation,
    });

    const localizedTzLabel = luxonTime.setLocale(locale).offsetNameShort;

    return { formattedTime, localizedTzLabel };
  };

  const startData = getLocalizedTimeData(start);
  const endData = getLocalizedTimeData(end);

  return (
    <span className="whitespace-nowrap">
      {t("timeRange", {
        start: startData.formattedTime,
        end: endData.formattedTime,
        abbreviation: startData.localizedTzLabel as string,
      })}
    </span>
  );
};

const MainTimeSlot = ({ todayHours, abbreviation }: MainTimeSlotProps) => {
  const t = useTranslations("Header");
  return (
    <div className={`${commonClasses} flex justify-center sm:justify-end`}>
      <HeaderClockIcon />
      <span className="font-medium px-1">{t("open")}</span>
      <TimeSlot
        start={todayHours[0].start}
        end={todayHours[0].end}
        abbreviation={abbreviation}
      />

      <DownArrowIcon />
    </div>
  );
};

const AdditionalTimeSlots = ({
  todayHours,
  abbreviation,
}: MainTimeSlotProps) => {
  return (
    <div className={`${commonClasses} pr-5`}>
      {todayHours.slice(1, 3).map((timeSlot: TimeSlot) => (
        <div key={timeSlot.start} className="whitespace-nowrap">
          <TimeSlot
            start={timeSlot.start}
            end={timeSlot.end}
            abbreviation={abbreviation}
          />
        </div>
      ))}
    </div>
  );
};

const MoreIndicator = () => {
  return (
    <div className={`${commonClasses} pr-5`}>
      <span className="ml-1 text-blue-600 hover:text-blue-800">...</span>
    </div>
  );
};

export const SimpleStorefrontHours = ({
  merchant,
  todayHours,
  abbreviation,
  headerBusinessHours,
  merchantPaymentConfig,
}: any) => {
  const t = useTranslations("Header");
  const [hoursModalIsOpen, setHoursModalIsOpen] = useState<boolean>(false);

  const handleHoursModal = () => {
    setHoursModalIsOpen(!hoursModalIsOpen);
  };

  const hasHours = todayHours.length > 0;

  return (
    <div className={"sm:flex flex-row justify-center items-right"}>
      <div className="text-center">
        <div className={`${commonClasses} flex flex-col pb-4`}>
          {hasHours ? (
            <button
              onClick={handleHoursModal}
              aria-haspopup="dialog"
              aria-pressed={hoursModalIsOpen}
              className="sm:text-right"
              type="button"
              aria-label={t("openBusinessHoursAlt")}
            >
              <MainTimeSlot
                todayHours={todayHours}
                abbreviation={abbreviation}
              />
              {todayHours.length > 1 && (
                <AdditionalTimeSlots
                  todayHours={todayHours}
                  abbreviation={abbreviation}
                />
              )}
              {todayHours.length > 3 && <MoreIndicator />}
            </button>
          ) : (
            <button
              className="flex justify-end font-medium px-1 pr-5 gap-[6px] items-center"
              onClick={handleHoursModal}
              aria-haspopup="dialog"
              aria-pressed={hoursModalIsOpen}
              type="button"
              aria-label={t("openBusinessHoursAlt")}
            >
              <ClockIcon height={14} width={14} />
              <span className="text-[15px] leading-6 font-bold">
                {t("closed")}
              </span>
              <ChevronDownIcon />
            </button>
          )}
          {merchantPaymentConfig &&
            merchantPaymentConfig.surcharging?.rate > 0 && (
              <Surcharge
                isTimeSlot={true}
                surchargeRate={merchantPaymentConfig.surcharging?.rate}
              />
            )}
        </div>
      </div>
      <BusinessHoursModal
        isOpen={hoursModalIsOpen}
        onRequestClose={handleHoursModal}
        businessHours={headerBusinessHours}
        timezone={abbreviation}
        isRestaurantMerchant={merchant?.isRestaurant}
      />
    </div>
  );
};

export const calculateCurrentDayPart = (
  isDineIn: boolean,
  dayPartMenus: DayPart[] = [],
  menuName: string | null = null
) => {
  if (!dayPartMenus || dayPartMenus.length === 0) return null;

  if (menuName && !isDineIn) {
    const preSelectedMenu = dayPartMenus.find(
      (menu) => menu.formattedName === menuName
    );
    if (preSelectedMenu) return preSelectedMenu;
  }

  const sessionDayPartMenu = getFromSessionStorage<{
    id?: string;
    menuId?: string;
  }>(SessionStorageKey.DAYPART_MENU, {});

  if (sessionDayPartMenu?.id && !isDineIn) {
    const sessionMenu = dayPartMenus.find(
      (menu) => menu.id === sessionDayPartMenu.id
    );
    if (sessionMenu) return sessionMenu;
  }

  const activeMenu = dayPartMenus.find((menu) => menu.activeNow);
  if (activeMenu) return activeMenu;

  const upcomingMenu = dayPartMenus.find((menu) => menu.upcoming);
  if (upcomingMenu) return upcomingMenu;

  return dayPartMenus[0];
};

export const Header = ({
  merchantLogo,
  merchantName,
  merchant,
  menu,
  deleteSession,
  retailCategories,
  serviceCategories,
  pickupTimePromise,
  redirectToGiftCard,
  dayPartMenus,
  dayPartMenuCategories,
  paymentConfigPromise,
  preferencesPromise,
}: HeaderProps) => {
  const [cookies] = useCookies(["daypartMenu"]);
  const t = useTranslations("Header");
  const tPickup = useTranslations("CalculatePickTime");
  const tUncategorized = useTranslations("category");
  const userLocale = useLocale();
  const { isCmaApp } = useNativeActions();

  const pathName = usePathname();
  const searchParams = useSearchParams();
  const [customerDetails, setCustomerDetails] = useState<any>(null);
  const [defaultAddress, setDefaultAddress] = useState<Address | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const orderType = searchParams?.get("order_type");
  const menuName = searchParams?.get("menu");
  const isDineIn = orderType?.toLowerCase() === OrderType.DINE_IN.toLowerCase();
  const merchantColor =
    merchant?.merchantMeta?.colorPalette?.color ||
    cloverColorTheme.Main.primaryColor;
  const isSimpleStorefront = merchant?.merchantMeta?.selectedSetup === "Simple";
  const isPreviewSite =
    typeof window !== "undefined" &&
    window.location.hostname.startsWith("preview-");
  const isStandaloneGiftCard =
    merchant?.isStandaloneGiftCardWebsite && !isPreviewSite;

  const [isScrolledDown, setIsScrolledDown] = useState(false);
  const [isCategoryMenuOpen, setIsCategoryMenuOpen] = useState<
    boolean | undefined
  >(false);
  const [selectedMenu, setSelectedMenu] = useState<DayPart | null>(null);

  const scrollRef = useRef(0);
  const { headerContentRef, recalculateHeight } = useHeaderHeight();
  const isMobileScreen = useMediaQuery("(max-width: 640px)");

  const currentDay = new Date()
    .toLocaleDateString("en-US", { weekday: "long" })
    .toLowerCase();
  const tableData = getFromLocalStorage(LocalStorageKey.TABLE_DATA, undefined);
  const scanToOrderInfo = getFromLocalStorage(
    LocalStorageKey.SCAN_TO_ORDER_INFO,
    {
      tableId: "",
      employeeId: "",
      orderType: OrderType.PICKUP as OrderType,
    }
  );
  const isScanToOrder = tableData && scanToOrderInfo;

  const merchantPaymentConfig =
    paymentConfigPromise && use(paymentConfigPromise);

  const merchantPreferences = preferencesPromise
    ? use(preferencesPromise)
    : null;

  useEffect(() => {
    const fetchInitialData = async () => {
      setIsLoading(true);
      try {
        const [address, details] = await Promise.all([
          getDefaultAddress(),
          getCustomerDetails(merchant.id),
        ]);
        setDefaultAddress(address);
        setCustomerDetails(details);
      } catch (error) {
        console.error("Failed to fetch initial data", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialData();
  }, [merchant.id]);

  useEffect(() => {
    const currentDayPart = calculateCurrentDayPart(
      isDineIn,
      dayPartMenus,
      menuName
    );

    if (currentDayPart) {
      const payload: DayPartMenuCookie = {
        id: currentDayPart.id,
        menuId: currentDayPart.menuId,
        name: currentDayPart.formattedName,
      };
      saveToSessionStorage(SessionStorageKey.DAYPART_MENU, payload);

      if (cookies?.daypartMenu?.id !== currentDayPart.id) {
        setDPMenuCookie(payload);
      }
    }

    setSelectedMenu(currentDayPart);
  }, [menuName, dayPartMenus]);

  const service = merchant?.services.find(
    (s: Service) =>
      s.type === ServiceType.PICKUP || s.type === ServiceType.WEBSITE
  );
  const headerBusinessHours = merchant?.isRestaurant
    ? service?.hours
    : merchant?.businessHours?.hours;

  useEffect(() => {
    const protectedPaths = [
      ROUTE_GUEST_CHECKOUT,
      ROUTE_CHECKOUT,
      ROUTE_CONFIRMATION,
      ROUTE_CUSTOMER_PROFILE,
      ROUTE_MY_ORDERS,
      ROUTE_MY_APPOINTMENTS,
    ];

    if (protectedPaths.includes(pathName!)) {
      return;
    }
    const handleBeforeUnload = () => {
      removeFromSessionStorage(SessionStorageKey.SCHEDULED_TIME);
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [pathName]);

  // To provide shrinking animation
  useEffect(() => {
    // Don't apply scroll effect on standalone gift card sites
    if (isStandaloneGiftCard) return;

    const node = document.querySelector("main");

    const handleScroll = () => {
      const scrollTop = node?.scrollTop || 0;
      const offset = 25;

      if (scrollTop > scrollRef.current + offset) {
        setIsScrolledDown(true);
        scrollRef.current = scrollTop;
      } else if (scrollTop < scrollRef.current - offset || scrollTop === 0) {
        setIsScrolledDown(false);
        scrollRef.current = scrollTop;
      }
    };

    const throttledHandleScroll = throttle(handleScroll, 450, {
      leading: true,
      trailing: true,
    });

    node?.addEventListener("scroll", throttledHandleScroll);

    return () => {
      node?.removeEventListener("scroll", throttledHandleScroll);
    };
  }, [isStandaloneGiftCard]);

  const abbreviation = getTimezoneAbbreviation(merchant?.timezone, userLocale);

  const renameUncategorized = (cat: Category) =>
    cat.id === UNCATEGORISED_CATEGORY_ID
      ? { ...cat, name: tUncategorized("uncategorized") }
      : cat;

  const renamedServiceCategories = useMemo(
    () => serviceCategories.map(renameUncategorized),
    [serviceCategories]
  );

  const renamedRetailCategories = useMemo(
    () => retailCategories.map(renameUncategorized),
    [retailCategories]
  );

  // Use  headerRegistry to resolve special headers for routes (dependency injection / service locator)
  const routeRenderer = headerRegistry[pathName ?? ""];
  if (typeof routeRenderer === "function") {
    const rendered = routeRenderer({
      pathName,
      searchParams,
      isScanToOrder,
      redirectToGiftCard,
      merchant,
    });
    if (rendered !== undefined) return rendered;
  }

  const todayHours = headerBusinessHours?.[currentDay] ?? [];

  const isAcceptingOrders = canMerchantAcceptOrders(
    merchant,
    isDineIn,
    selectedMenu,
    tPickup
  );

  const isNotAcceptingOrder = !isAcceptingOrders || !merchant.isCheckoutEnabled;

  if (isCmaApp) {
    return (
      <CmaHeader
        todaysHours={todayHours}
        businessHours={headerBusinessHours}
        merchantPaymentConfig={merchantPaymentConfig}
        isNotAcceptingOrders={isNotAcceptingOrder}
        selectedMenu={selectedMenu}
        pickupTimePromise={pickupTimePromise}
        isLoading={isLoading}
        defaultAddress={defaultAddress}
        retailCategories={renamedRetailCategories}
        serviceCategories={renamedServiceCategories}
        setIsCategoryMenuOpen={setIsCategoryMenuOpen}
        isCategoryMenuOpen={isCategoryMenuOpen}
        dayPartMenu={dayPartMenus}
        dayPartMenuCategory={dayPartMenuCategories}
        setSelectedMenu={setSelectedMenu}
        menu={menu}
        customerDetails={customerDetails}
        merchantPreferences={merchantPreferences}
      />
    );
  }

  return (
    <nav
      className={clsx(
        "flex",
        "flex-col",
        "items-center",
        "w-full",
        "top-0",
        "z-10",
        "bg-[#FBFBFB]",
        {
          "pt-3": !isMobileScreen && isScrolledDown,
          absolute: headerContentRef.current,
        }
      )}
      ref={headerContentRef}
      data-testid={applyDataTestId(pom.header.headerContainer)}
    >
      <CookiesBanner />
      <div
        className={clsx(
          "flex",
          "sm:flex-row",
          "gap-4",
          "px-4 sm:px-10",
          "pt-6",
          "w-full",
          "justify-between",
          "transition-all",
          "ease-out",
          "duration-[0.3s]",
          {
            "flex-col": isSimpleStorefront,
            "pt-0": isMobileScreen && isScrolledDown,
            "pt-5": isMobileScreen && !isScrolledDown,
            "max-h-0 opacity-0 absolute left-[-9999px]": isScrolledDown,
            "max-h-none relative opacity-1": !isScrolledDown,
            "pb-5": isStandaloneGiftCard,
          }
        )}
        aria-hidden={isScrolledDown}
      >
        <HeaderMerchantInfoContainer
          merchantLogo={merchantLogo}
          merchantName={merchantName}
          isSimpleStorefront={isSimpleStorefront}
          isStandaloneGiftCard={isStandaloneGiftCard}
          merchantAddress={merchant?.address}
          merchantPhone={merchant?.phone}
          onLogoLoad={() => {
            if (pathName === ROUTE_CONFIRMATION) {
              setTimeout(() => recalculateHeight(), 500);
            }
          }}
        />
        {isSimpleStorefront && pathName !== ROUTE_CONFIRMATION && (
          <SimpleStorefrontHours
            merchant={merchant}
            todayHours={todayHours}
            abbreviation={merchant.timezone}
            headerBusinessHours={headerBusinessHours}
            merchantPaymentConfig={merchantPaymentConfig}
          />
        )}
        {pathName !== ROUTE_CONFIRMATION &&
          !isSimpleStorefront &&
          !isStandaloneGiftCard && (
            <HeaderLinkButton
              merchant={merchant}
              merchantColor={merchantColor}
              selectedDayPart={selectedMenu}
            />
          )}
        {pathName === ROUTE_CONFIRMATION && (
          <p className="hidden sm:block font-normal text-[15px]">
            <MerchantLinkButton
              merchantColor={merchantColor}
              uri={ROUTE_HOME}
              text={t("keepBrowsing")}
            />
          </p>
        )}
      </div>
      {pathName !== ROUTE_CONFIRMATION && !isStandaloneGiftCard && (
        <div
          className={clsx("w-full", {
            "pt-3": !isMobileScreen && isScrolledDown,
          })}
        >
          <NavigationBar
            menu={menu}
            customerPerks={customerDetails?.earnedPerks}
            merchantPreferences={merchantPreferences}
            deleteSession={deleteSession}
            retailCategories={renamedRetailCategories}
            serviceCategories={renamedServiceCategories}
            pickupTimePromise={pickupTimePromise}
            defaultAddress={defaultAddress}
            isLoading={isLoading}
            setIsCategoryMenuOpen={setIsCategoryMenuOpen}
            isCategoryMenuOpen={isCategoryMenuOpen}
            dayPartMenus={dayPartMenus}
            dayPartMenuCategories={dayPartMenuCategories}
            selectedMenu={selectedMenu}
            setSelectedMenu={setSelectedMenu}
            isNotAcceptingOrder={isNotAcceptingOrder}
            isScrolledDown={isScrolledDown}
          />
        </div>
      )}
      <NotAcceptingOrdersBanner
        merchant={merchant}
        isNotAcceptingOrder={isNotAcceptingOrder}
        selectedDayPart={selectedMenu}
      />
    </nav>
  );
};

const HeaderLinkButton = ({
  merchant,
  merchantColor,
  selectedDayPart,
}: any) => {
  const t = useTranslations("Header");
  const pathName = usePathname();
  const isNotRestaurantOfferShown = !pathName
    ?.toLocaleLowerCase()
    .includes("menu");
  const isNotEssentialMerchantOfferShown = !pathName
    ?.toLocaleLowerCase()
    .includes("categories");
  const isOnGiftCardPage = pathName === ROUTE_GIFTCARD;
  const scanToOrderInfo = getFromLocalStorage(
    LocalStorageKey.SCAN_TO_ORDER_INFO,
    null
  );
  let queryParams = "";
  if (scanToOrderInfo) {
    queryParams = buildScanToOrderSearchParams(scanToOrderInfo);
  }
  let menuUri = merchant?.isEssentialMerchant
    ? ROUTE_CATEGORIES_ALL
    : ROUTE_MENU_ALL;

  if (!isEmpty(selectedDayPart))
    queryParams += queryParams
      ? `&menu=${selectedDayPart.formattedName}`
      : `?menu=${selectedDayPart.formattedName}`;

  if (queryParams) {
    menuUri += queryParams;
  }

  const linkButtonConfig = (() => {
    if (merchant?.isService && merchant?.hasOnlyRetailItems) {
      // Hide "Shop Now" when already on the shop pages
      if (pathName?.startsWith(ROUTE_SHOP)) return null;
      return { uri: ROUTE_SHOP, text: t("shopNow") };
    }
    if (merchant?.isService && !merchant?.hasOnlyRetailItems) {
      // Hide "Book Now" when already on the services pages
      if (pathName?.startsWith(ROUTE_SERVICES)) return null;
      return { uri: ROUTE_SERVICES_ALL, text: t("bookNow") };
    }
    if (merchant?.isRetail) {
      // Hide "Shop Now" when already on the all pages
      if (pathName?.startsWith(ROUTE_ALL)) return null;
      return { uri: ROUTE_ALL, text: t("shopNow") };
    }
    if (merchant?.isRestaurant && isNotRestaurantOfferShown) {
      return { uri: menuUri, text: t("startOrder") };
    }
    if (
      merchant?.isEssentialMerchant &&
      !merchant?.isCheckoutEnabled &&
      isNotEssentialMerchantOfferShown
    ) {
      return { uri: menuUri, text: t("viewItems") };
    }
    return null;
  })();

  return (
    <div className={["flex gap-6 items-center"].join(" ")}>
      <div className={isOnGiftCardPage ? "hidden md:block" : ""}>
        {linkButtonConfig && (
          <MerchantLinkButton
            merchantColor={merchantColor}
            uri={linkButtonConfig.uri}
            text={linkButtonConfig.text}
          />
        )}
      </div>
    </div>
  );
};
